import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { isCustomerMode } from '../../config/customerMode';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import AppCard from '../landing/AppCard';

/**
 * App launcher (`/tileapps`).
 *
 * Rewritten to show ONLY the deployed apps (Condo Manager, PestPulse Pro, EHS
 * Guardian) with self-contained inline-SVG artwork and real links. It makes no
 * external network requests, so it no longer "loads endlessly" (the previous
 * version pulled Google Fonts + clearbit logos that could hang). In customer
 * mode it self-filters to the CondoPro tile only, as before.
 */
const AppDashboard: React.FC = () => {
  const apps = isCustomerMode()
    ? DEPLOYED_APPS.filter(a => a.id === 'condo')
    : DEPLOYED_APPS;

  return (
    <Page>
      <Hero>
        <Blob />
        <div>
          <Eyebrow>Workspace</Eyebrow>
          <Title>Applications</Title>
          <Subtitle>Launch any of your deployed apps.</Subtitle>
        </div>
        <OverviewLink to="/products">Product overview →</OverviewLink>
      </Hero>

      <Grid>
        {apps.map(app => (
          <AppCard key={app.id} app={app} compact />
        ))}
      </Grid>
    </Page>
  );
};

export default AppDashboard;

// ── styled ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px;
  min-height: 100%;
  background: #EAEEEF;
  font-family: 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #14222B;
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: linear-gradient(135deg, #15524C 0%, #0E3F3A 58%, #0B342F 100%);
  border-radius: 22px;
  padding: 26px 28px;
  margin-bottom: 22px;
  box-shadow: 0 8px 20px rgba(14,63,58,.34);
`;

const Blob = styled.div`
  position: absolute;
  top: -70px;
  right: -40px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(80,190,175,.18);
  pointer-events: none;
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .07em;
  text-transform: uppercase;
  color: rgba(214,240,235,.82);
`;

const Title = styled.h1`
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -.02em;
  color: #ffffff;
  margin: 2px 0 4px;
`;

const Subtitle = styled.p`
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(214,240,235,.92);
  margin: 0;
`;

const OverviewLink = styled(Link)`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  color: #0E3F3A;
  background: #ffffff;
  transition: transform .12s ease;

  &:active { transform: scale(.98); }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 18px;
`;
